import { Price, PriceAdd, PriceHis } from "@/types/sales";
import {
  isArray,
  isPrice,
  isPriceHis,
  isString,
  isStringEmpty,
} from "@/utils/checkTool";
import request from ".";
import { cleanToPrice, cleanToPriceHis } from "@/utils/cleanData";
import { CommonListParams } from "@/types/common";

const prefixUrl = "/wms/price";

const apis = {
  add: prefixUrl + `/add`,
  sAdd: prefixUrl + `/s/add`,
  get: (id: string) => prefixUrl + `/${id}/get`,
  fetch: (id: string) => prefixUrl + `/${id}/fetch`,
  del: (id: string) => prefixUrl + `/${id}/del`,
  list: prefixUrl + `/list`,
  fetchs: prefixUrl + `/fetchs`,
  update: (id: string) => prefixUrl + `/${id}/update`,
  updateByKey: (id: string, key: PriceUpdateKey) =>
    prefixUrl + `/${id}/${key}/update`,
  hisList: (id: string) => prefixUrl + `/${id}/his/list`,
  hisFetchs: (id: string) => prefixUrl + `/${id}/his/fetchs`,
  latestList: prefixUrl + `/latest/his/list`,
  latestFetchs: prefixUrl + `/latest/his/fetchs`,
};

type PriceUpdateKey = "price" | "desc";

export const sAdd = (price: PriceAdd, supplierApi: string) => {
  if (price.price && !isString(price.price)) {
    return Promise.reject(10012);
  }
  return request<Price, PriceAdd>(apis.sAdd, {
    params: { body: price },
    requestConfig: supplierApi ? { baseURL: supplierApi } : undefined
  })
    .then(({ body: price }) => {
      if (isPrice(price)) {
        return Promise.resolve(cleanToPrice(price));
      } else {
        return Promise.reject(10013);
      }
    })
    .catch((err) => Promise.reject(err));
};

export const add = (price: PriceAdd) => {
  if (price.price && !isString(price.price)) {
    return Promise.reject(10012);
  }
  return request<Price, PriceAdd>(apis.add, {
    params: { body: price },
  })
    .then(({ body: price }) => {
      if (isPrice(price)) {
        return Promise.resolve(cleanToPrice(price));
      } else {
        return Promise.reject(10013);
      }
    })
    .catch((err) => Promise.reject(err));
};

export const get = (id: string) => {
  if (!isString(id) || isStringEmpty(id)) {
    return Promise.reject(10012);
  }
  return request<Price, string>(apis.get(id))
    .then(({ body: price }) => {
      if (isPrice(price)) {
        return Promise.resolve(cleanToPrice(price));
      } else {
        return Promise.reject(10013);
      }
    })
    .catch((err) => Promise.reject(err));
};

export const fetch = (id: string) => {
  if (!isString(id) || isStringEmpty(id)) {
    return Promise.reject(10012);
  }
  return request<Price, string>(apis.fetch(id))
    .then(({ body: price }) => {
      if (isPrice(price)) {
        return Promise.resolve(cleanToPrice(price));
      } else {
        return Promise.reject(10013);
      }
    })
    .catch((err) => Promise.reject(err));
};

export const del = (id: string) => {
  return new Promise<void>((resolve, reject) => {
    if (!isString(id) || isStringEmpty(id)) {
      return Promise.reject(10012);
    }
    request<void, void>(apis.del(id))
      .then(() => resolve())
      .catch((err) => reject(err));
  });
};

export const list = (params?: CommonListParams<string>) => {
  if (params && params.body && !isString(params.body)) {
    return Promise.reject(10012);
  }
  return request<Array<Price>, string>(apis.list, { params })
    .then(({ body: price, pagination }) => {
      if (isArray<Price>(price)) {
        return Promise.resolve({
          body: price
            .filter((price) => isPrice(price))
            ?.map((price) => cleanToPrice(price)),
          pagination,
        });
      } else {
        return Promise.reject(10013);
      }
    })
    .catch((err) => Promise.reject(err));
};

export const fetchs = (params?: CommonListParams) => {
  if (params && params.body && !isString(params.body)) {
    return Promise.reject(10012);
  }
  return request<Array<Price>, string>(apis.fetchs, { params })
    .then(({ body: price }) => {
      if (isArray<Price>(price)) {
        return Promise.resolve({
          body: price
            .filter((price) => isPrice(price))
            ?.map((price) => cleanToPrice(price)),
        });
      } else {
        return Promise.reject(10013);
      }
    })
    .catch((err) => Promise.reject(err));
};

export const update: (props: PriceAdd, id: string) => Promise<void> = (
  props,
  id
) => {
  return new Promise((resolve, reject) => {
    if (
      !isString(id) ||
      (isStringEmpty(id) && props.price && !isString(props.price))
    ) {
      return reject(10012);
    }
    request<void, PriceAdd>(apis.update(id), {
      params: { body: props },
    })
      .then(() => resolve())
      .catch(() => reject());
  });
};

export interface UpdateByKey {
  key: PriceUpdateKey;
  value: string;
}

export const updateByKey = (id: string, props: UpdateByKey) => {
  return new Promise<void>((resolve, reject) => {
    if (
      (!isString(id) || isStringEmpty(id)) &&
      props.key &&
      !isString(props.key)
    ) {
      return reject(10012);
    }
    request<void, string>(apis.updateByKey(id, props.key), {
      params: { body: props.value },
    })
      .then(() => resolve())
      .catch((err) => reject(err));
  });
};

export const priceHisList = (id: string) => {
  if (!isString(id) || isStringEmpty(id)) {
    return Promise.reject(10012);
  }
  return request<Array<PriceHis>, string>(apis.hisList(id))
    .then((body) => {
      return Promise.resolve(body);
    })
    .catch((err) => Promise.reject(err));
};

export const priceHisFetchs = (id: string) => {
  if (!isString(id) || isStringEmpty(id)) {
    return Promise.reject(10012);
  }
  return request<PriceHis, string>(apis.hisFetchs(id))
    .then(({ body: price }) => {
      if (isPriceHis(price)) {
        return Promise.resolve(cleanToPrice({ ...price, logBookId: "" }));
      } else {
        return Promise.reject(10013);
      }
    })
    .catch((err) => Promise.reject(err));
};

export const priceLatestList = (params?: CommonListParams<string>) => {
  if (params && params.body && !isString(params.body)) {
    return Promise.reject(10012);
  }
  return request<Array<PriceHis>, string>(apis.latestList, { params })
    .then(({ body: price, pagination }) => {
      if (isArray<PriceHis>(price)) {
        return Promise.resolve({
          body: price
            .filter((price) => isPriceHis(price))
            ?.map((price) => cleanToPriceHis({ ...price })),
          pagination,
        });
      } else {
        return Promise.reject(10013);
      }
    })
    .catch((err) => Promise.reject(err));
};

export const priceLatestFetchs = (params?: CommonListParams) => {
  if (params && params.body && !isString(params.body)) {
    return Promise.reject(10012);
  }
  return request<Array<PriceHis>, string>(apis.latestFetchs, { params })
    .then(({ body: price }) => {
      if (isArray<PriceHis>(price)) {
        return Promise.resolve({
          body: price
            .filter((price) => isPriceHis(price))
            ?.map((price) => cleanToPrice({ ...price, logBookId: "" })),
        });
      } else {
        return Promise.reject(10013);
      }
    })
    .catch((err) => Promise.reject(err));
};

const exportObject = {
  list,
  fetchs,
  add,
  del,
  get,
  fetch,
  updateByKey,
  update,
  priceHisList,
  priceHisFetchs,
  priceLatestList,
  priceLatestFetchs,
};

export type { PriceUpdateKey };

export default exportObject;
